import axios from "axios";
import {
  DATA_SVC_RS_ADDRESS,
  PAD_SCENARIO_SERVICE_ADDRESS
} from "constants/app.constants";
import { FOCUS_SYNC_OPERATOR_PROPERTY } from "utils/color/focusColorSync/triggers";

import type { mBinSize } from "../models/binSize";
import { EntityKind } from "../models/entityKind";
import type { GroupModeOptionsDto, PerformanceDateRange } from "../models/groupBy";
import { INormalizeBySetting } from "../models/normalizeBy";
import { PdenSourceEnum } from "../models/pdenDataSourceSetting";
import {
  DataQueryParamsType,
  FetchDataRequestType,
  FetchDataResponseSchema,
  FetchDataResponseType,
  ForecastTrumpParamsType
} from "../models/schemas/data.schema";
import { Result } from "./dataSource";

const DATA_RS_ENDPOINT = `${DATA_SVC_RS_ADDRESS}/api/v1/data`;
export const PYTHON_COLUMN_ENDPOINT = `${DATA_SVC_RS_ADDRESS}/api/v1/user-defined-column`;
export const USER_COLUMN_RS_ENDPOINT = `${DATA_SVC_RS_ADDRESS}/api/v1/user-defined-column`;

type DataQueryParamsT = {
  pdenSource: number;
  pageSize: number;
  pageIndex: number;
  sortColumn?: string;
  sortDirection?: string;
  entityKind?: string;
};

export interface NormalizationParams {
  useNormalizeBy: boolean;
  globalNormalizeBy?: INormalizeBySetting;
}

// Maps frontend string AggregationType enum values to the backend's proto integer values.
// Proto values (groupby.proto): Default=0, Sum=1, Average=2, Min=3, Max=4, MostCommon=5, JoinDistinct=6
const AGGREGATION_TYPE_TO_INT: Record<string, number> = {
  Default: 0,
  Sum: 1,
  Average: 2,
  Min: 3,
  Max: 4,
  MostCommon: 5,
  JoinDistinct: 6
};

export function aggregationTypeToInt(aggregation: string | undefined): number {
  if (!aggregation) return 0;
  return AGGREGATION_TYPE_TO_INT[aggregation] ?? 0;
}

// "Dynamic" is a UI-only BinType that drives the UseDynamicBins flag.
// The backend BinType enum only has BinSize | Quantile — send BinSize for the Dynamic case.
function normalizeBinTypeForRequest(bin: mBinSize): mBinSize {
  if (bin.BinType !== "Dynamic") return bin;
  return { ...bin, BinType: "BinSize" };
}

// The frontend uses "" as the "unset" sentinel for numeric bin fields.
// BinSize/MinSize/MaxBins are required numerics — convert "" | null | undefined → 0.
// LessThan/GreaterThan are optional bounds — must be sent as JSON null when the
// form input is blank so the backend treats that side as unbounded (Option<f64>
// deserializes null as None). Numeric 0 is a valid explicit bound and passes through;
// stale-sentinel `0` values (e.g. from sessionStorage) should be fixed at the write site.
function sanitizeBinForRequest(bin: mBinSize): mBinSize {
  const toNumberOrZero = (v: number | string | null | undefined): number => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : 0;
  };
  // Only coerce empty-string / null / undefined → null.
  // Numeric 0 is a valid bound (e.g. latitude, temperature) and must pass through.
  // Stale-sentinel `0` values from sessionStorage should be fixed at the write site,
  // not silently swallowed here.
  const toBoundOrNull = (v: number | string | null | undefined): number | null => {
    if (v === "" || v === null || v === undefined) return null;
    const n = typeof v === "string" ? Number(v) : v;
    if (!Number.isFinite(n)) return null;
    return n;
  };
  return {
    ...bin,
    BinSize: toNumberOrZero(bin.BinSize),
    MinSize: toNumberOrZero(bin.MinSize),
    MaxBins: toNumberOrZero(bin.MaxBins),
    GreaterThan: toBoundOrNull(bin.GreaterThan),
    LessThan: toBoundOrNull(bin.LessThan)
  };
}

// API-layer DTO for group mode options.
// Uses integer aggregation (matching the backend i32 field) rather than
// the string-enum used internally in the frontend store.
export interface GroupModeRequestDto {
  property: string;
  bin?: mBinSize;
  aggregation?: number; // integer, not AggregationType string enum
  /** When true, return individual wells but replace the group-by property
   *  value with its bin label (e.g. "3000 - 3100" instead of "3071").
   *  Only sent in well-view when a numeric/date field is binned. */
  wellView?: boolean;
}

export interface FetchDataOptions {
  normalization?: NormalizationParams;
  defaultSignificantFigures?: number;
  useStrictRounding?: boolean;
  forecastTrumpingSettings?: ForecastTrumpParamsType;
  groupMode?: GroupModeRequestDto; // REQ-UI-TOG-003a
  performanceDateRange?: PerformanceDateRange;
}

/**
 * Converts a frontend GroupModeOptionsDto (string AggregationType) to a
 * GroupModeRequestDto (integer aggregation) for the API layer.
 */
/**
 * Returns true when group mode has a meaningful, configured bin that warrants
 * sending `wellView: true` to the backend (i.e. the frozen "Group" column
 * should show bin labels rather than raw property values).
 *
 * Guards:
 *   - bin must be non-null (not the emptyBin sentinel)
 *   - BinSize must be a non-empty, non-zero value (emptyBin has BinSize: "")
 *   - canBin must be true (non-binable numerics must not use this path)
 *   - dataType must be Number, Integer, or Date (per REQ-UI-GRP-003)
 */
export function isActiveBinnedGroupMode(mode: GroupModeOptionsDto | undefined): boolean {
  if (!mode?.bin) return false;
  if (mode.bin.BinSize === "" || mode.bin.BinSize === 0) return false;
  if (!mode.canBin) return false;
  const t = mode.dataType;
  return t === "Number" || t === "Integer" || t === "Date";
}

export function toGroupModeRequestDto(
  mode: GroupModeOptionsDto | undefined
): GroupModeRequestDto | undefined {
  if (!mode) return undefined;
  return {
    property: mode.property,
    bin: mode.bin
      ? sanitizeBinForRequest(normalizeBinTypeForRequest(mode.bin))
      : undefined,
    aggregation: aggregationTypeToInt(mode.aggregation)
  };
}

export function fetchData(
  filterId: string,
  fields: string[],
  params: DataQueryParamsT,
  options?: FetchDataOptions
): Promise<FetchDataResponseType> {
  const normalization =
    options?.normalization?.useNormalizeBy && options.normalization.globalNormalizeBy
      ? {
          ...options.normalization.globalNormalizeBy,
          useNormalizeBy: true as const
        }
      : undefined;

  const body: FetchDataRequestType = {
    filterId,
    fields,
    entityKind: params.entityKind,
    outputFormatType: "JSON",
    defaultSignificantFigures: options?.defaultSignificantFigures,
    useStrictRounding: options?.useStrictRounding,
    normalization,
    forecastTrumpingSettings: options?.forecastTrumpingSettings
  };
  if (options?.normalization?.useNormalizeBy && options.normalization.globalNormalizeBy) {
    body.normalization = {
      ...options.normalization.globalNormalizeBy,
      useNormalizeBy: options.normalization.useNormalizeBy
    };
  }
  if (options?.groupMode) {
    body.groupMode = options.groupMode;
  }
  if (options?.performanceDateRange) {
    body.performanceDateRange = options.performanceDateRange;
  }

  return axios.post(DATA_RS_ENDPOINT, body, { params }).then((res) => {
    const result = FetchDataResponseSchema.safeParse(res.data);
    if (!result.success) {
      throw new Error(
        `Unexpected fetchData response shape from ${DATA_RS_ENDPOINT}: ${result.error.message}`
      );
    }
    return result.data;
  });
}

export interface FocusColorSyncTranslationParams {
  filterId: string;
  // The focus-variant property (a short name, an exchange:symbol pair, or a
  // bare stock symbol) to pair against Header.Operator.
  variantProperty: string;
}

// One well's two values, already paired. Empty strings are kept as they came
// back; deciding what an unusable value means is the caller's job.
export interface FocusColorSyncTranslationPair {
  variantValue: string;
  publicRecordValue: string;
}

// Header.Operator and its variants are static well header fields, not
// production-source scoped, so which pdenSource is picked never changes the
// result — but the backend requires a valid one regardless (see below).
// PdenSourceEnum.Public is the app's own unset-selection default, the same
// value multiphase-chart/hooks/useChartOptionSetter.tsx already hardcodes
// for the same situation.
const FOCUS_TRANSLATION_PDEN_SOURCE = PdenSourceEnum.Public;

/**
 * POST /api/v1/data for exactly two fields — a focus-variant property and
 * Header.Operator — across every well the current filter matches, returned as
 * one pair per well. Backs useFocusTranslation (hooks/focusColorSync), which
 * folds the pairs into a lookup from an operator-variant legend value (short
 * name, exchange:symbol pair, bare symbol) to the public-record operator name
 * a rig or frac record always carries.
 *
 * The response is keyed by WELL, not by field: each key is a UWI and each value
 * is that well's requested fields in the order they were asked for (the service
 * builds the row by zipping `fields` with the resolved columns and splitting on
 * a null separator). So the pairing here is positional — index 0 is the variant,
 * index 1 is the public-record name — and reading it as a column-per-key record
 * silently finds nothing at all.
 *
 * The backend's pagination extractor (`Query<Pagination>`, eva-data-svc-rs's
 * data_options.rs) declares pageIndex/pageSize as non-`Option` with no
 * `#[serde(default)]`, and pdenSource with a deserializer that errors when
 * absent — an empty query string 400s before the handler even runs, so none
 * of the three are optional here. `pageSize: 0` is the service's documented
 * "return everything" signal, which is what lets this pull every matching
 * well's two fields in one call.
 */
export async function fetchFocusColorSyncTranslation(
  params: FocusColorSyncTranslationParams
): Promise<FocusColorSyncTranslationPair[]> {
  const { filterId, variantProperty } = params;
  // Order matters: it decides the position of each value in every row below.
  const properties = [variantProperty, FOCUS_SYNC_OPERATOR_PROPERTY];
  const VARIANT_INDEX = 0;
  const PUBLIC_RECORD_INDEX = 1;

  const body: FetchDataRequestType = {
    filterId,
    fields: properties,
    entityKind: EntityKind.Well,
    outputFormatType: "JSON"
  };

  const queryParams: DataQueryParamsType = {
    pdenSource: FOCUS_TRANSLATION_PDEN_SOURCE,
    pageSize: 0,
    pageIndex: 0,
    entityKind: EntityKind.Well
  };

  const response = await axios.post(DATA_RS_ENDPOINT, body, { params: queryParams });
  const rows = response.data;
  if (!rows || typeof rows !== "object" || Array.isArray(rows)) {
    throw new Error(
      `Unexpected focus translation response from ${DATA_RS_ENDPOINT}: expected an object keyed by well`
    );
  }

  // The one shape change that would otherwise pass silently. Everything below
  // reads each row positionally, so a response keyed by FIELD rather than by
  // well still looks valid: the values are arrays, they are long enough, and
  // pairing their first two entries yields a confident mapping of one well's
  // operator to another's. It would be wrong in a way nothing downstream can
  // see. Requesting a field whose name turns up as a key is proof of that
  // shape, so it fails here instead.
  const requestedFieldKeys = properties.filter((property) =>
    Object.prototype.hasOwnProperty.call(rows, property)
  );
  if (requestedFieldKeys.length > 0) {
    throw new Error(
      `Unexpected focus translation response from ${DATA_RS_ENDPOINT}: keyed by field ` +
        `(${requestedFieldKeys.join(", ")}) rather than by well, so rows cannot be paired`
    );
  }

  // Deliberately not zod: `pageSize: 0` means this response carries a row for
  // every well the filter matches, and a schema pass over it would walk the
  // whole payload and then keep a second validated copy of it, for a result
  // whose useful size is the number of legend bins. The loop below already
  // rejects anything it can't use, one row at a time and without copying.
  const pairs: FocusColorSyncTranslationPair[] = [];
  let malformedRowCount = 0;
  for (const row of Object.values(rows as Record<string, unknown>)) {
    // A row shorter than the fields asked for can't be paired positionally, so
    // it is dropped rather than guessed at. A well with no value for a field
    // still gets its slot, as an empty string, so this is about shape only.
    if (!Array.isArray(row) || row.length <= PUBLIC_RECORD_INDEX) {
      malformedRowCount += 1;
      continue;
    }
    pairs.push({
      variantValue: String(row[VARIANT_INDEX] ?? ""),
      publicRecordValue: String(row[PUBLIC_RECORD_INDEX] ?? "")
    });
  }

  // Nothing usable at all is a shape problem, not a data one, and it is the
  // only case worth failing over: a single odd row should not cost the user
  // every colour. The message counts rows rather than naming them, because the
  // names here are well identifiers and the count is what is diagnostic.
  if (pairs.length === 0 && malformedRowCount > 0) {
    throw new Error(
      `Unexpected focus translation response shape from ${DATA_RS_ENDPOINT}: ` +
        `${malformedRowCount} rows, none with the two requested fields`
    );
  }
  return pairs;
}

export interface ValueTranslationParams {
  property: string;
  values: string[];
  targetProperty: string;
}

export interface ValueTranslationRow {
  value: string;
  targetValue: string;
  wellCount: number;
}

// Filter-independent, unlike fetchFocusColorSyncTranslation: an operator
// excluded by the main filter has no wells left in the filter to pair against.
export async function fetchValueTranslation(
  params: ValueTranslationParams
): Promise<ValueTranslationRow[]> {
  const url = `${DATA_RS_ENDPOINT}/value-translation`;
  const response = await axios.post(url, { ...params, entityKind: EntityKind.Well });
  const rows = response.data;
  if (!Array.isArray(rows)) {
    throw new Error(
      `Unexpected value translation response from ${url}: expected an array`
    );
  }
  return rows.map((row) => {
    if (
      !row ||
      typeof row.value !== "string" ||
      typeof row.targetValue !== "string" ||
      typeof row.wellCount !== "number"
    ) {
      throw new Error(
        `Unexpected value translation response from ${url}: row missing value, targetValue or wellCount`
      );
    }
    return { value: row.value, targetValue: row.targetValue, wellCount: row.wellCount };
  });
}

export async function fetchTypeLogData() {
  return axios
    .get(`${DATA_RS_ENDPOINT}/typeLog`)
    .then((res) => res.data)
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Error fetching type log data:", error);
    });
}

export interface ColumnModel {
  name: string;
  group: string;
  id?: string;
  note?: string;
  units: string;
  data: { [name: string]: string };
  pythonScript: string;
  entityKind?: EntityKind;
  normalizeByDefault: string;
  /** Display order within the sub-group. The create endpoint expects this exact
   *  casing; the /order endpoint uses `withinSubgroupOrder` instead. */
  withinSubGroupOrder?: number;
}

export interface RawDataColumn {
  /** "" for unnamed columns (present in rawDataColumns, excluded from columns). */
  headerName: string;
  isKeyHeader: boolean;
  data: string[];
}

export interface UploadExcelResult {
  columns: ColumnModel[];
  uwis: string[];
  uwiHeaderName: string;
  rawDataColumns: RawDataColumn[];
  headerRowIndex: number;
  /** Post-dedup names of data columns whose stored data contained at least one genuine Excel error. */
  columnsWithErrors: string[];
  /** Used-range indices of blank-headered columns; indexes 1:1 into rawDataColumns. */
  unnamedColumns: number[];
}

export interface ExportDataRequest {
  filterId: string;
  properties: string[];
  format: "CSV" | "Excel";
  forecastSource: string;
  pdenSource?: number;
  syncJob?: string;
  entityKind?: EntityKind;
  normalization?: NormalizationParams;
  groupMode?: GroupModeRequestDto; // REQ-UI-TOG-005
  performanceDateRange?: PerformanceDateRange;
}

export interface InfoRequest {
  filterId?: string;
  uniqueId: string;
  aggregateProperty: string;
  aggregate: string;
  forecastFolder?: string;
  pdenSource?: number;
  performanceDateRange?: PerformanceDateRange;
  defaultSignificantFigures?: number;
  useStrictRounding?: boolean;
}

export interface WellDetailCardRequest {
  uwid: string;
  group?: string;
  fields: string[];
  entityKind?: EntityKind;
  defaultSignificantFigures?: number;
  useStrictRounding?: boolean;
  forecastTrumpingSettings?: ForecastTrumpParamsType;
}
export interface WellDetailCardData {
  [name: string]: string | Blob;
}

export interface WellDetailCardDataGroup {
  group: string;
  wellData: WellDetailCardData;
}
export interface WellCardTypeLogPdf {
  group: string;
  wellData: Blob;
}

export interface WellListDataRequest {
  uwis: string[];
  fields: string[];
  pdenSource?: number;
}

export async function postPythonColumn(
  data: ColumnModel,
  isOrg: boolean,
  projectId?: string | null
): Promise<Result<boolean>> {
  const projectIdParam = projectId ? `&projectId=${projectId}` : "";
  const response = await axios.post(
    `${PYTHON_COLUMN_ENDPOINT}/python?isOrg=${isOrg ? "true" : "false"}${projectIdParam}`,
    data
  );
  return response.data;
}

export async function postUserDefinedColumn(
  data: ColumnModel[],
  isOrg: boolean,
  projectId?: string | null,
  entityKind: EntityKind = EntityKind.Well
): Promise<Result<boolean>> {
  const projectIdParam = projectId ? `&projectId=${projectId}` : "";
  const response = await axios.post(
    `${USER_COLUMN_RS_ENDPOINT}?isOrg=${
      isOrg ? "true" : "false"
    }${projectIdParam}&entityKind=${entityKind}`,
    data
  );
  return response.data;
}

export async function uploadCustomColumnFile(
  file,
  keyHeader: string,
  entityKind: EntityKind
): Promise<UploadExcelResult> {
  const formData = new FormData();

  formData.append("file", file);
  const response = await axios.post<UploadExcelResult>(
    `${USER_COLUMN_RS_ENDPOINT}/upload?keyHeader=${keyHeader}&entityKind=${entityKind}`,
    formData
  );
  return response.data;
}

export async function deleteUserDefinedColumn(
  columnIds: string[],
  isOrg: boolean,
  entityKind: EntityKind = EntityKind.Well,
  isProjectColumn: boolean
): Promise<boolean> {
  const response = await axios.delete<boolean>(
    `${USER_COLUMN_RS_ENDPOINT}?isOrg=${
      isOrg ? "true" : "false"
    }&entityKind=${entityKind.toString()}&isProjectColumn=${
      isProjectColumn ? "true" : "false"
    }`,
    {
      data: columnIds
    }
  );
  return response.data;
}

export async function updateUserDefinedColumnOrder(
  data,
  isOrg: boolean,
  entityKind: EntityKind = EntityKind.Well,
  groupName?: string,
  projectId?: string,
  isProjectDefined?: boolean
): Promise<Result<boolean>> {
  const params = new URLSearchParams({
    isOrg: isOrg ? "true" : "false",
    entityKind
  });
  if (groupName) {
    params.append("fieldGroup", groupName);
  }
  // Only include projectId for Project Fields (isProjectDefined=true)
  if (isProjectDefined && projectId) {
    params.append("projectId", projectId);
  }
  const response = await axios.post(
    `${USER_COLUMN_RS_ENDPOINT}/order?${params.toString()}`,
    data
  );
  return response.data;
}

export async function editUserDefinedColumn(
  data: ColumnModel,
  isOrg: boolean,
  entityKind: EntityKind = EntityKind.Well,
  projectId?: string | null
): Promise<Result<boolean>> {
  const projectIdParam = projectId ? `&projectId=${projectId}` : "";
  const response = await axios.put(
    `${USER_COLUMN_RS_ENDPOINT}?isOrg=${
      isOrg ? "true" : "false"
    }&entityKind=${entityKind}${projectIdParam}`,
    data
  );
  return response.data;
}

export interface CellUpdate {
  propertyName: string;
  uwi: string;
  value: string;
}

export interface NewColumn {
  name: string;
  defaultUnit?: string;
}

export interface NewRow {
  uwi: string;
  data: Record<string, string>;
}

export interface BulkUpdateRequest {
  entityKind: EntityKind;
  fieldGroup: string;
  isOrg: boolean;
  projectId?: string | null;
  updates: CellUpdate[];
  newColumns: NewColumn[];
  newRows: NewRow[];
}

export interface BulkUpdateResponse {
  success: boolean;
}

export async function bulkUpdateFieldGroup(
  request: BulkUpdateRequest,
  entityKind: EntityKind,
  isOrg: boolean,
  projectId?: string | null
): Promise<BulkUpdateResponse> {
  const projectIdParam = projectId ? `&projectId=${projectId}` : "";
  const response = await axios.post<BulkUpdateResponse>(
    `${USER_COLUMN_RS_ENDPOINT}/bulk-update?entityKind=${entityKind}&isOrg=${
      isOrg ? "true" : "false"
    }${projectIdParam}`,
    request
  );
  return response.data;
}

export async function downloadColumnTemplate(
  filterId: string,
  entityKind: EntityKind
): Promise<Result<string>> {
  const cleanedFilterId = filterId === "empty-filter-id" ? "" : filterId;

  const response = await axios.get(
    `${USER_COLUMN_RS_ENDPOINT}/export?filterId=${cleanedFilterId}&exportFormat=Excel&entityKind=${entityKind}`,
    {
      responseType: "blob"
    }
  );
  if (response.status === 200) {
    const encodedUri = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = encodedUri;
    link.download = "eva-field-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return {
      ok: true,
      value: ""
    };
  }
  return {
    ok: false,
    error: response.data
  };
}

export async function exportData(request: ExportDataRequest) {
  const body: Record<string, unknown> = {
    filterId: request.filterId,
    fields: request.properties,
    outputFormatType: "JSON",
    entityKind: request.entityKind
  };

  if (request.normalization?.useNormalizeBy && request.normalization.globalNormalizeBy) {
    body.normalization = {
      ...request.normalization.globalNormalizeBy,
      useNormalizeBy: request.normalization.useNormalizeBy
    };
  }
  if (request.groupMode) {
    body.groupMode = request.groupMode;
  }
  if (request.performanceDateRange) {
    body.performanceDateRange = request.performanceDateRange;
  }

  // Omit syncJob entirely when unset — interpolating undefined would send the
  // literal string "undefined" as a schema name to the backend.
  const syncJobParam = request.syncJob
    ? `&syncJob=${encodeURIComponent(request.syncJob)}`
    : "";

  return await axios.post(
    `${DATA_RS_ENDPOINT}/export?exportFormat=${request.format}&forecastSource=${request.forecastSource}&pdenSource=${request.pdenSource}${syncJobParam}`,
    body,
    {
      responseType: "blob"
    }
  );
}

export async function getSingleWellInfo(request: InfoRequest) {
  return await axios.post(`${DATA_RS_ENDPOINT}/single-well/info`, request);
}

export async function getWellDetailCard(request: WellDetailCardRequest) {
  if (request.group === "Type Log") {
    return await axios.post(
      `${DATA_RS_ENDPOINT}/single/${request.uwid}`,
      {
        fields: request.fields
      },
      {
        responseType: "arraybuffer"
      }
    );
  }
  return await axios.post(`${DATA_RS_ENDPOINT}/single/${request.uwid}`, {
    fields: request.fields,
    outputFormatType: "JSON",
    entityKind: request.entityKind,
    defaultSignificantFigures: request.defaultSignificantFigures,
    useStrictRounding: request.useStrictRounding,
    forecastTrumpingSettings: request.forecastTrumpingSettings
  });
}

export async function getWellData(request: WellListDataRequest) {
  return await axios.post(`${DATA_RS_ENDPOINT}/from-well-list`, {
    uwis: request.uwis,
    fields: request.fields,
    pdenSource: request.pdenSource
  });
}

export async function updateGroupOrder(
  data,
  isOrg: boolean,
  projectId?: string
): Promise<Result<boolean>> {
  let url = `${USER_COLUMN_RS_ENDPOINT}/groupOrder?isOrg=${isOrg ? "true" : "false"}`;
  if (projectId) {
    url += `&projectId=${projectId}`;
  }
  const response = await axios.post(url, data);
  return response.data;
}

export async function getGroupOrder(isOrg: boolean) {
  const response = await axios.get(
    `${USER_COLUMN_RS_ENDPOINT}/groupOrder?isOrg=${isOrg ? "true" : "false"}`
  );
  return response.data;
}

export async function getValidWells(wells: string[]): Promise<string[]> {
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi`;
  const response = await axios.post<string[]>(endpoint, wells);
  return response.data;
}

export async function getValidSticks(sticks: string[]): Promise<string[]> {
  const endpoint = `${PAD_SCENARIO_SERVICE_ADDRESS}/api/v1/sticks/validate`;
  const response = await axios.post<string[]>(endpoint, sticks);
  return response.data;
}

/** A row of GET user-defined-column/geombins. Both data-svc backends spell the
 *  order key `withinSubGroupOrder` on this route. */
export interface GeomBinListItem {
  id: string;
  group: string;
  name: string;
  withinSubGroupOrder: number;
}

export interface GeomBinModel {
  id: string;
  group: string;
  name: string;
  isDeleted: boolean;
  nodes: GeomBinNodeModel[];
}

export interface GeomBinNodeModel {
  id: string;
  binId?: string;
  name: string;
  order: number;
  geom: string;
  isDeleted: boolean;
  color: string;
  strokeColor: string;
  opacity: number;
  thickness: number;
}

export async function postGeomBin(
  data: GeomBinModel,
  isOrg: boolean,
  projectId?: string
): Promise<Result<boolean>> {
  let url = `${USER_COLUMN_RS_ENDPOINT}/geombin?isOrg=${isOrg ? "true" : "false"}`;

  if (projectId) {
    url += `&projectId=${projectId}`;
  }

  const response = await axios.post(url, data);
  return response.data;
}

export async function deleteGeomBins(
  ids: string[],
  projectId?: string
): Promise<boolean> {
  let url = `${USER_COLUMN_RS_ENDPOINT}/geombin`;

  if (projectId) {
    url += `?projectId=${projectId}`;
  }

  const response = await axios.delete<boolean>(url, {
    data: ids
  });
  return response.data;
}

export async function updateGeomBinOrder(
  data,
  groupName?: string,
  projectId?: string,
  isProjectDefined?: boolean
): Promise<Result<boolean>> {
  let params = "";
  if (groupName || (isProjectDefined && projectId)) {
    const urlParams = new URLSearchParams();
    if (groupName) urlParams.append("fieldGroup", groupName);
    // Only include projectId for Project Fields (isProjectDefined=true)
    if (isProjectDefined && projectId) urlParams.append("projectId", projectId);
    params = `?${urlParams.toString()}`;
  }
  const response = await axios.post(
    `${USER_COLUMN_RS_ENDPOINT}/geombin/order${params}`,
    data
  );
  return response.data;
}

export async function getGeomBin(binId: string): Promise<Result<boolean>> {
  const url = `${USER_COLUMN_RS_ENDPOINT}/geombin/${binId}`;

  const response = await axios.get(url);
  return response.data;
}

export async function updateGeomBinGroupOrder(
  data,
  isOrg: boolean,
  projectId?: string
): Promise<Result<boolean>> {
  let url = `${USER_COLUMN_RS_ENDPOINT}/geombin/groupOrder/?isOrg=${
    isOrg ? "true" : "false"
  }`;
  if (projectId) {
    url += `&projectId=${projectId}`;
  }
  const response = await axios.post(url, data);
  return response.data;
}

export async function getGeomBinGroupOrder(isOrg: boolean, projectId?: string) {
  let url = `${USER_COLUMN_RS_ENDPOINT}/geombin/groupOrder/?isOrg=${
    isOrg ? "true" : "false"
  }`;

  if (projectId) {
    url += `&projectId=${projectId}`;
  }

  const response = await axios.get(url);
  return response.data;
}
